import { createReducer } from '@reduxjs/toolkit';
import {
  CLEAR_FINISHED_DOWNLOADS,
  CLOSE_DOWNLOAD_TRAY,
  DOWNLOAD_FILE_DONE,
  DOWNLOAD_FILE_ERROR,
  DOWNLOAD_FILE_PROGRESS,
  DOWNLOAD_FILE_START,
  DOWNLOAD_SESSION_START,
  TOGGLE_DOWNLOAD_TRAY,
} from './actions';
import {
  DownloadBatchState,
  DownloadFileIndexPayload,
  DownloadFileProgressPayload,
  DownloadFileStartPayload,
  DownloadFileState,
  DownloadFileStatus,
  DownloadStartPayload,
  DownloadsState,
} from './models';
import { isBatchRunning } from './summarize';
import { defaultState, namespace } from './store';

/**
 * Apply `change` to one file of one batch, leaving everything else untouched.
 *
 * Both keys matter. Three files within a batch download at once, so events
 * interleave; and two batches can run at once, so an index on its own is
 * ambiguous — without the batch id, the first download's progress would land on
 * the second download's rows and corrupt a transfer the user is watching.
 *
 * An event for a batch or index that is not there is ignored rather than
 * creating one: it can only mean events outliving what declared them (a batch
 * the user cleared while its streams were still draining), and inventing a row
 * from a stale event would show a phantom file in the tray.
 */
const patchFile = (
  state: DownloadsState,
  batchId: string,
  index: number,
  change: (file: DownloadFileState) => DownloadFileState
): DownloadsState => {
  const batchIndex = state.batches.findIndex((batch) => batch.id === batchId);
  const batch = state.batches[batchIndex];
  if (!batch) {
    return state;
  }
  const current = batch.files[index];
  if (!current || current.index !== index) {
    return state;
  }
  const files = [...batch.files];
  files[index] = change(current);
  const batches = [...state.batches];
  batches[batchIndex] = { ...batch, files };
  return { ...state, batches };
};

const sessionStart = (
  state: DownloadsState,
  { payload }: { payload: DownloadStartPayload }
): DownloadsState => {
  const batch: DownloadBatchState = {
    id: payload.batchId,
    files: payload.files.map((file, index) => ({
      index,
      name: file.name,
      status: 'queued' as DownloadFileStatus,
      loaded: 0,
      total: file.sizeBytes,
    })),
  };
  return {
    ...state,
    // Appended, not replacing: a batch started while another is running is a
    // separate transfer and both have to keep reporting.
    batches: [...state.batches, batch],
  };
};

const clearFinished = (state: DownloadsState): DownloadsState => {
  const batches = state.batches.filter(isBatchRunning);
  return {
    ...state,
    batches,
    isTrayOpen: batches.length === 0 ? false : state.isTrayOpen,
  };
};

const fileStart = (
  state: DownloadsState,
  { payload }: { payload: DownloadFileStartPayload }
): DownloadsState =>
  patchFile(state, payload.batchId, payload.index, (file) => ({
    ...file,
    status: 'downloading',
    // `?? file.total`, matching fileProgress: today the seed and this event come
    // from the same DownloadTarget so an undefined here means the seed was
    // undefined too, but a caller seeding the size from anywhere else would
    // otherwise watch the denominator vanish the moment the file started.
    total: payload.total ?? file.total,
  }));

const fileProgress = (
  state: DownloadsState,
  { payload }: { payload: DownloadFileProgressPayload }
): DownloadsState =>
  patchFile(state, payload.batchId, payload.index, (file) => ({
    ...file,
    // A file already marked done stays done: its final event and a throttled
    // in-flight one can arrive in either order.
    status: file.status === 'done' ? 'done' : 'downloading',
    loaded: Math.max(file.loaded, payload.loaded),
    total: payload.total ?? file.total,
  }));

const fileDone = (
  state: DownloadsState,
  { payload }: { payload: DownloadFileIndexPayload }
): DownloadsState =>
  patchFile(state, payload.batchId, payload.index, (file) => ({
    ...file,
    status: 'done',
    // Nothing more is coming, so a known size is now also the amount written.
    // Without this a file whose last throttled progress event landed at 99%
    // would sit at 99% in the tray forever.
    loaded: file.total ?? file.loaded,
  }));

const fileError = (
  state: DownloadsState,
  { payload }: { payload: DownloadFileIndexPayload }
): DownloadsState =>
  patchFile(state, payload.batchId, payload.index, (file) => ({
    ...file,
    status: 'error',
  }));

const toggleTray = (state: DownloadsState): DownloadsState => ({
  ...state,
  isTrayOpen: !state.isTrayOpen,
});

const closeTray = (state: DownloadsState): DownloadsState => ({
  ...state,
  isTrayOpen: false,
});

export const downloadsReducer = {
  [namespace]: createReducer(defaultState, (builder) => {
    builder
      .addCase(DOWNLOAD_SESSION_START, sessionStart)
      .addCase(CLEAR_FINISHED_DOWNLOADS, clearFinished)
      .addCase(DOWNLOAD_FILE_START, fileStart)
      .addCase(DOWNLOAD_FILE_PROGRESS, fileProgress)
      .addCase(DOWNLOAD_FILE_DONE, fileDone)
      .addCase(DOWNLOAD_FILE_ERROR, fileError)
      .addCase(TOGGLE_DOWNLOAD_TRAY, toggleTray)
      .addCase(CLOSE_DOWNLOAD_TRAY, closeTray);
  }),
};
